package com.evoapps.evoshift.domain.usecase.shift

import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.domain.model.BreakException
import com.evoapps.evoshift.domain.model.BreakRules
import com.evoapps.evoshift.domain.model.ClockSource
import com.evoapps.evoshift.domain.model.DailyRecordIds
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.model.RosterPublishing
import com.evoapps.evoshift.domain.model.ShiftBreak
import com.evoapps.evoshift.domain.model.TimeRange
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.usecase.CalculateShiftCostUseCase
import kotlinx.datetime.LocalDate

/**
 * Edits a shift; a published shift keeps serving the old version until the roster is republished.
 * [isOpen] offers an unassigned shift to the team (see [com.evoapps.evoshift.domain.model.Shift.isOpen]);
 * it is ignored for assigned shifts, and un-marking withdraws an offer already on the marketplace.
 */
class UpdateShiftUseCase(
    private val shiftRepository: ShiftRepository,
    /** Reprices the shift for its new assignee (financial managers; best effort, see SH-12). */
    private val calculateShiftCost: CalculateShiftCostUseCase? = null
) {
    suspend operator fun invoke(
        shiftId: String,
        userId: String?,
        userDisplayName: String?,
        requiredRole: String?,
        requiredSkills: List<String>,
        plannedTime: TimeRange,
        actualTime: TimeRange?,
        editorUserId: String,
        isOpen: Boolean = false,
        /** SH-116: the manager's corrected breaks; null keeps them (cleared clock times clear them). */
        breaks: List<ShiftBreak>? = null,
        /**
         * SH-169: the shift moved to this day of its month (dragged in the grid): its day and daily
         * record follow; [plannedTime] is then that day's. Null keeps the day.
         */
        date: LocalDate? = null
    ): Result<Unit> = runCatching {
        require(plannedTime.start < plannedTime.end) { "Zmiana musi kończyć się po rozpoczęciu" }
        val shift = shiftRepository.getShift(shiftId).getOrThrow()
        val clocked = actualTime?.start?.isNotEmpty() == true
        val newBreaks = when {
            !clocked -> emptyList()
            breaks == null -> shift.breaks
            else -> breaks.map { if (it in shift.breaks) it else it.copy(source = ClockSource.MANAGER) }.sortedBy { it.start }
        }
        if (breaks != null && clocked) {
            BreakRules.validate(newBreaks, actualTime!!.start, actualTime.end)?.let { throw BreakException(it) }
        }
        val open = userId == null && isOpen
        val withdrawOffer = shift.isOpen && !open && shift.userId == null
        val day = date?.toString() ?: shift.date
        shiftRepository.updateShift(
            shift.copy(
                date = day,
                dailyRecordId = if (date == null) shift.dailyRecordId
                    else DailyRecordIds.of(RosterIds.workplaceIdOf(shift.rosterId), day),
                userId = userId,
                // SH-271: given to someone else, the next publish tells who had it.
                publishedUserId = RosterPublishing.publishedAssignee(shift, userId),
                isOpen = open,
                swapRequest = if (withdrawOffer) null else shift.swapRequest,
                // A forced double booking was about the person: an emptied shift has none.
                conflictMetadata = if (userId == null) null else shift.conflictMetadata,
                userDisplayName = userDisplayName,
                requiredRole = requiredRole,
                requiredSkills = requiredSkills,
                plannedTime = plannedTime,
                actualTime = actualTime,
                breaks = newBreaks,
                version = shift.version + 1,
                hasUnpublishedChanges = shift.isPublished || shift.hasUnpublishedChanges,
                metadata = shift.metadata.copy(updatedAt = AppClock.now().toString(), updatedBy = editorUserId)
            )
        ).getOrThrow()
        calculateShiftCost?.invoke(shiftId)
    }
}
